import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import leaveService from '../../../services/leaveService';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { formatDateFromApi } from '../../../utils/attendanceDateTime';

const LeaveSection = ({ onNotify }) => {
  const { employee, leaves, leaveBalance, holidays, reloadLeaves } = useEmployeeSelfService();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ leave_date: '', end_date: '', leave_type: 'casual', reason: '' });

  useEffect(() => {
    if (employee?.id) reloadLeaves();
  }, [employee?.id, reloadLeaves]);

  const getBalance = (type) => leaveBalance.find((b) => b.leave_type === type)?.available ?? null;

  const countDays = (start, end) => {
    const s = new Date(start);
    const e = new Date(end || start);
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee?.id) return;
    const endDate = form.end_date || form.leave_date;
    const days = countDays(form.leave_date, endDate);
    const available = getBalance(form.leave_type);
    if (available !== null && form.leave_type !== 'unpaid' && days > available) {
      onNotify?.({ message: `Insufficient ${form.leave_type} leave balance`, type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const result = await leaveService.createLeave({
        employee_id: employee.id,
        leave_date: form.leave_date,
        end_date: endDate,
        leave_type: form.leave_type,
        reason: form.reason,
        status: 'pending',
      });
      if (result.success) {
        onNotify?.({ message: 'Leave application submitted', type: 'success' });
        setShowForm(false);
        setForm({ leave_date: '', end_date: '', leave_type: 'casual', reason: '' });
        await reloadLeaves();
      } else {
        onNotify?.({ message: result.error || 'Failed to submit', type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'approved') return 'bg-green-100 text-green-800';
    if (status === 'rejected') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Leave Management</h2>
          <p className="text-sm text-muted-foreground">Same leave records as Employee Management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={reloadLeaves}>Refresh</Button>
          <Button onClick={() => setShowForm(true)} iconName="Plus">Apply Leave</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {leaveBalance.map((b) => (
          <div key={b.leave_type} className="bg-card border border-border rounded-lg p-3 text-center">
            <p className="text-xs capitalize text-muted-foreground">{b.leave_type}</p>
            <p className="text-lg font-bold">{b.available}</p>
            <p className="text-xs text-muted-foreground">of {b.quota} left</p>
          </div>
        ))}
      </div>

      {holidays.length > 0 && (
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-2">Upcoming Holidays</h3>
          <div className="flex flex-wrap gap-2">
            {holidays.slice(0, 6).map((h) => (
              <span key={h.id} className="text-xs bg-card border px-2 py-1 rounded">
                {formatDateFromApi(h.holiday_date)} — {h.holiday_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={handleSubmit} className="bg-card rounded-lg border w-full max-w-lg p-6 space-y-4">
            <h3 className="font-semibold">Apply for Leave</h3>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" required className="border rounded px-3 py-2" value={form.leave_date} onChange={(e) => setForm({ ...form, leave_date: e.target.value })} />
              <input type="date" className="border rounded px-3 py-2" value={form.end_date} min={form.leave_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <select className="w-full border rounded px-3 py-2" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
              <option value="casual">Casual</option>
              <option value="sick">Sick</option>
              <option value="earned">Earned</option>
              <option value="unpaid">Unpaid</option>
            </select>
            <textarea required rows={3} className="w-full border rounded px-3 py-2" placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit'}</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Reason</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((leave) => (
              <tr key={leave.id} className="border-t">
                <td className="px-4 py-2">{formatDateFromApi(leave.leave_date)}</td>
                <td className="px-4 py-2 capitalize">{leave.leave_type}</td>
                <td className="px-4 py-2">{leave.reason || '-'}</td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs ${statusColor(leave.status)}`}>{leave.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {leaves.length === 0 && <p className="p-6 text-center text-muted-foreground">No leave history</p>}
      </div>
    </div>
  );
};

export default LeaveSection;
